import { IFieldModel } from '@checkout/fields';
import { IAddressFields, ICountry, IState } from '@checkout/state';
import { IntlProvider } from 'react-intl';
import { DropDownField, InputField } from '../../presentational';
import messages from './../../../i18n/base-en';
import { alphanumeric, minLength, required } from './../../../utils/validators';

export const getAddressFormModel = (fields: IAddressFields, formName: string, intlData: IntlProvider.Props, disableCountryStateFields?: boolean) => {
  const { intl } = new IntlProvider({ ...intlData }, {}).getChildContext();
  const { formatMessage } = intl;

  const addersFormModel: IFieldModel[] = [
    {
      component: InputField,
      type: 'text',
      name: 'firstName',
      placeholder: formatMessage(messages.first_name_placeholder),
      label: formatMessage(messages.first_name_label),
      required: fields.firstName.required,
      visible: fields.firstName.visible,
      validate: (value: string) => {
        let error;
        const requiredError = required(value, formatMessage(messages.required_valid_error));
        const minLengthError = minLength(value, 2, formatMessage(messages.min_length_error, { min: 2 }));

        if (fields.firstName.required && requiredError) {
          error = requiredError;
        } else if (minLengthError) {
          error = minLengthError;
        }

        return error;
      },
      sizeVariant: 'half',
    },
    {
      component: InputField,
      type: 'text',
      name: 'lastName',
      placeholder: formatMessage(messages.last_name_placeholder),
      label: formatMessage(messages.last_name_label),
      required: fields.lastName.required,
      visible: fields.lastName.visible,
      validate: (value: string) => {
        let error;
        const requiredError = required(value, formatMessage(messages.required_valid_error));
        const minLengthError = minLength(value, 2, formatMessage(messages.min_length_error, { min: 2 }));

        if (fields.lastName.required && requiredError) {
          error = requiredError;
        } else if (minLengthError) {
          error = minLengthError;
        }

        return error;
      },
      sizeVariant: 'half',
    },
    {
      component: InputField,
      type: 'text',
      name: 'company',
      placeholder: formatMessage(messages.company_placeholder),
      label: formatMessage(messages.company_label),
      required: fields.company.required,
      visible: fields.company.visible,
      validate: (value: string) => {
        let error;
        const requiredError = required(value, formatMessage(messages.required_valid_error));
        const minLengthError = minLength(value, 2, formatMessage(messages.min_length_error, { min: 2 }));

        if (fields.company.required && requiredError) {
          error = requiredError;
        } else if (minLengthError) {
          error = minLengthError;
        }

        return error;
      },
    },
    {
      component: InputField,
      type: 'text',
      name: 'address1',
      placeholder: formatMessage(messages.address_1_placeholder),
      label: formatMessage(messages.address_1_label),
      required: fields.address1.required,
      visible: fields.address1.visible,
      validate: (value: string) => {
        let error;
        const requiredError = required(value, formatMessage(messages.required_valid_error));
        const minLengthError = minLength(value, 2, formatMessage(messages.min_length_error, { min: 2 }));

        if (fields.address1.required && requiredError) {
          error = requiredError;
        } else if (minLengthError) {
          error = minLengthError;
        }

        return error;
      },
      sizeVariant: 'medium',
    },
    /* { // API is always returning visible: false for this field
      component: TextInput,
      type: 'text',
      name: houseNumber',
      placeholder: formatMessage(messages.houseNumber_placeholder),
      label: formatMessage(messages.houseNumber_label),
      required: fields.houseNumber.required,
      visible: fields.houseNumber.visible,
      sizeVariant: 'small',
    }, */
    {
      component: InputField,
      type: 'text',
      name: 'address2',
      placeholder: formatMessage(messages.address_2_placeholder),
      label: formatMessage(messages.address_2_label),
      required: fields.address2.required,
      visible: fields.address2.visible,
      validate: (value: string) => {
        let error;
        const requiredError = required(value, formatMessage(messages.required_valid_error));
        const minLengthError = minLength(value, 2, formatMessage(messages.min_length_error, { min: 2 }));

        if (fields.address2.required && requiredError) {
          error = requiredError;
        } else if (minLengthError) {
          error = minLengthError;
        }

        return error;
      },
      sizeVariant: 'small'
    },
    {
      component: InputField,
      type: 'text',
      name: 'city',
      placeholder: formatMessage(messages.city_placeholder),
      label: formatMessage(messages.city_label),
      required: fields.city.required,
      visible: fields.city.visible,
      validate: (value: string) => {
        let error;
        const requiredError = required(value, formatMessage(messages.required_valid_error));
        const minLengthError = minLength(value, 2, formatMessage(messages.min_length_error, { min: 2 }));
        const alphanumericError = alphanumeric(value, formatMessage(messages.alphanumeric_valid_error));

        if (fields.city.required && requiredError) {
          error = requiredError;
        } else if (minLengthError) {
          error = minLengthError;
        } else if (alphanumericError) {
          error = alphanumericError;
        }

        return error;
      },
      sizeVariant: 'medium',
    },
    {
      component: InputField,
      type: 'text',
      name: 'zipCode',
      placeholder: formatMessage(messages.zip_code_placeholderl),
      label: formatMessage(messages.zip_code_label),
      required: fields.zipCode.required,
      visible: fields.zipCode.visible,
      validate: (value: string) => {
        let error;
        const requiredError = required(value, formatMessage(messages.required_valid_error));
        const minLengthError = minLength(value, 2, formatMessage(messages.min_length_error, { min: 2 }));

        if (fields.zipCode.required && requiredError) {
          error = requiredError;
        } else if (minLengthError) {
          error = minLengthError;
        }

        return error;
      },
      sizeVariant: 'small',
    },
    {
      component: DropDownField,
      type: 'select',
      dropdownFor: 'Country',
      name: 'country',
      placeholder: formatMessage(messages.country_dropdown_placeholder),
      label: formatMessage(messages.country_label),
      required: fields.country.required,
      visible: fields.country.visible,
      validate: (value: string) => {
        let error;
        const requiredError = required(value, formatMessage(messages.required_valid_error));

        if (fields.country.required && requiredError) {
          error = requiredError;
        }

        return error;
      },
      valueSelector: (item: ICountry & IState) => item.country,
      sizeVariant: 'medium',
      disabled: disableCountryStateFields,
    },
    {
      component: DropDownField,
      type: 'select',
      dropdownFor: 'State',
      name: 'state',
      placeholder: formatMessage(messages.state_dropdown_placeholder),
      label: formatMessage(messages.state_label),
      required: fields.state.visible,
      visible: fields.state.visible,
      validate: (value: string) => {
        let error;
        const requiredError = required(value, formatMessage(messages.required_valid_error));

        if (fields.state.visible && requiredError) {
          error = requiredError;
        }

        return error;
      },
      valueSelector: (item: ICountry & IState) => item.state,
      sizeVariant: 'small',
      disabled: disableCountryStateFields,
    },
    /*
      missing fields:
        vatNumber - InputField
        houseExtension - InputField
    */
  ];

  /* TODO: add identityNumber field on the top of the form */
  /* if (fields.identityNumber) {
    addersFormModel.unshift({
      component: IdentityNumber,
      type: 'text',
      name: identityNumberFetch',
      placeholder: formatMessage(messages.personal_number_placeholder),
      label: formatMessage(messages.personal_number_label),
      required: fields.identityNumber.required,
      visible: fields.identityNumber.visible,
    });
  } */

  return addersFormModel;
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/container/AddressForm/AddressFormModel.ts